/**
 * Graphe d'évolution DISCRET (SVG pur) : une ou plusieurs séries tracées sur un
 * axe X étiqueté (matchs du tournoi, journées de groupe…). Sert les modales
 * « évolution d'une stat » des tables Équipes / Joueurs / Groupes — le pendant,
 * sur un axe par MATCH/JOURNÉE, du `statGraph` (axe par minute, intra-match).
 *
 * 1 série → une courbe lissée remplie ; N séries (nations d'un groupe) → N courbes
 * colorées + légende. Fonction PURE (testable) ; couleurs via variables CSS.
 */
import { esc } from "../html.ts";

export interface SeriesDatum {
  /** Étiquette de l'abscisse (adversaire, « J1 »…). */
  readonly x: string;
  readonly y: number | null;
}
export interface ChartSeries {
  readonly label: string;
  /** Couleur CSS (var(--…)) ; index dans la palette si omis. */
  readonly color?: string;
  readonly points: readonly SeriesDatum[];
}

const W = 320;
const H = 184;
const PAD = { l: 30, r: 12, t: 14, b: 34 };
const PALETTE = [
  "var(--color-text-success)",
  "var(--color-text-warning)",
  "var(--color-text-info, #6db3f2)",
  "var(--color-text-error, #e0726f)",
];

type Pt = readonly [number, number];

/** Catmull-Rom → Bézier lissé (repris de statGraph). */
function smooth(pts: readonly Pt[]): string {
  const first = pts[0];
  if (!first) return "";
  const f = (n: number): string => n.toFixed(1);
  if (pts.length === 1) return `M${f(first[0])},${f(first[1])}`;
  let d = `M${f(first[0])},${f(first[1])}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    if (!p0 || !p1 || !p2 || !p3) continue;
    d += ` C${f(p1[0] + (p2[0] - p0[0]) / 6)},${f(p1[1] + (p2[1] - p0[1]) / 6)} ${f(p2[0] - (p3[0] - p1[0]) / 6)},${f(p2[1] - (p3[1] - p1[1]) / 6)} ${f(p2[0])},${f(p2[1])}`;
  }
  return d;
}

const fmt = (v: number, asPct: boolean): string =>
  asPct ? `${Math.round(v)}%` : Number.isInteger(v) ? `${v}` : v.toFixed(2);

/**
 * Rend le graphe. `xLabels` = abscisses communes (ordonnées). Chaque série porte
 * une valeur (ou null = trou) par abscisse. `asPct` borne l'axe Y à 0–100.
 */
export function seriesChart(
  xLabels: readonly string[],
  series: readonly ChartSeries[],
  opts: { asPct?: boolean; emptyMsg?: string } = {},
): string {
  const asPct = opts.asPct ?? false;
  const nX = xLabels.length;
  const vals = series.flatMap((s) => s.points.map((p) => p.y)).filter((v): v is number => v != null);
  if (nX < 1 || vals.length < 1) return `<div class="sc-empty">${esc(opts.emptyMsg ?? "—")}</div>`;

  const x0 = PAD.l;
  const x1 = W - PAD.r;
  const yTop = PAD.t;
  const yBase = H - PAD.b;
  const sx = (i: number): number => (nX === 1 ? (x0 + x1) / 2 : x0 + (i / (nX - 1)) * (x1 - x0));
  const yMax = asPct ? 100 : Math.max(1, Math.ceil(Math.max(...vals) * 1.15));
  const yMin = !asPct && Math.min(...vals) < 0 ? Math.floor(Math.min(...vals) * 1.15) : 0;
  const sy = (v: number): number => yBase - ((v - yMin) / (yMax - yMin || 1)) * (yBase - yTop);

  // axes + graduations Y (0 et max)
  const grid =
    `<line class="sc-axis" x1="${x0}" y1="${yBase.toFixed(1)}" x2="${x1}" y2="${yBase.toFixed(1)}"/>` +
    `<text x="${x0 - 5}" y="${(sy(yMax) + 3).toFixed(1)}" class="sc-tick" text-anchor="end">${fmt(yMax, asPct)}</text>` +
    `<text x="${x0 - 5}" y="${(sy(yMin) + 3).toFixed(1)}" class="sc-tick" text-anchor="end">${fmt(yMin, asPct)}</text>` +
    (yMin < 0 ? `<line class="sc-zero" x1="${x0}" y1="${sy(0).toFixed(1)}" x2="${x1}" y2="${sy(0).toFixed(1)}"/>` : "");

  // étiquettes X (toutes si peu nombreuses, sinon ~6 réparties)
  const step = nX <= 8 ? 1 : Math.ceil(nX / 6);
  const xticks = xLabels
    .map((lab, i) =>
      i % step === 0 || i === nX - 1
        ? `<text x="${sx(i).toFixed(1)}" y="${H - 18}" class="sc-tick sc-xtick" text-anchor="middle">${esc(lab)}</text>`
        : "",
    )
    .join("");

  const lines = series
    .map((s, si) => {
      const color = s.color ?? PALETTE[si % PALETTE.length];
      const pts: Pt[] = s.points
        .map((p, i): Pt | null => (p.y == null ? null : [sx(i), sy(p.y)]))
        .filter((p): p is Pt => p !== null);
      if (!pts.length) return "";
      const dots = pts.map((p) => `<circle cx="${p[0].toFixed(1)}" cy="${p[1].toFixed(1)}" r="2.6" fill="${color}"/>`).join("");
      return `<path class="sc-line" d="${smooth(pts)}" stroke="${color}" pathLength="1"/>${dots}`;
    })
    .join("");

  const legend =
    series.length > 1
      ? `<div class="sc-legend">${series
          .map(
            (s, si) =>
              `<span class="sc-k"><span class="sc-sw" style="background:${s.color ?? PALETTE[si % PALETTE.length]}"></span>${esc(s.label)}</span>`,
          )
          .join("")}</div>`
      : "";

  return `<svg viewBox="0 0 ${W} ${H}" class="sc-svg" role="img" aria-label="${esc(series.map((s) => s.label).join(", "))}">
    ${grid}
    <g class="sc-reveal">${lines}</g>
    ${xticks}
  </svg>${legend}`;
}
